import dayjs from "utils/dayjs";
import type { TFunction } from "i18next";
import type { ChipTone } from "@outpave/ds";
import type {
  PlatformRewardRule,
  RewardRuleStatus,
} from "components/platform/rewards/types";
import { formatCashFull } from "utils/formatCash";

export const REWARD_RULE_STATUS_TONE: Record<RewardRuleStatus, ChipTone> = {
  LIVE: "positive",
  SCHEDULED: "info",
  ENDED: "neutral",
  PAUSED: "warning",
};

export const REWARD_RULE_STATUS_LABEL: Record<RewardRuleStatus, string> = {
  LIVE: "Live",
  SCHEDULED: "Scheduled",
  ENDED: "Ended",
  PAUSED: "Paused",
};

type RuleShape = Pick<
  PlatformRewardRule,
  | "scope"
  | "categoryName"
  | "merchantName"
  | "companies"
  | "ratePercent"
  | "startsOn"
  | "endsOn"
  | "monthlyCap"
  | "capCurrency"
>;

export const formatRuleDay = (day: string, withYear = true) =>
  dayjs.utc(day).format(withYear ? "MMM D, YYYY" : "MMM D");

export function ruleTarget(rule: RuleShape, t: TFunction): string {
  if (rule.scope === "CATEGORY") {
    return t("RewardRuleTargetCategory", { name: rule.categoryName ?? "" });
  }
  if (rule.scope === "MERCHANT") {
    return t("RewardRuleTargetMerchant", { name: rule.merchantName ?? "" });
  }
  return t("All merchants");
}

export function ruleCompanies(rule: RuleShape, t: TFunction): string {
  if (!rule.companies.length) return t("All accounts");
  if (rule.companies.length === 1) {
    return rule.companies[0].name ?? t("RewardRuleAccountCount", { count: 1 });
  }
  return t("RewardRuleAccountCount", { count: rule.companies.length });
}

export function ruleWindow(rule: RuleShape, t: TFunction): string {
  if (!rule.startsOn || !rule.endsOn) return t("Always on");
  const sameYear =
    dayjs.utc(rule.startsOn).year() === dayjs.utc(rule.endsOn).year();
  return t("RewardRuleWindow", {
    start: formatRuleDay(rule.startsOn, !sameYear),
    end: formatRuleDay(rule.endsOn),
  });
}

export function ruleSummary(rule: RuleShape, t: TFunction): string {
  const who = rule.companies.length
    ? ruleCompanies(rule, t)
    : t("every account");
  const what =
    rule.scope === "CATEGORY"
      ? t("RewardRuleSummaryCategory", { name: rule.categoryName ?? "" })
      : rule.scope === "MERCHANT"
        ? t("RewardRuleSummaryMerchant", { name: rule.merchantName ?? "" })
        : t("every purchase");
  const when =
    rule.startsOn && rule.endsOn
      ? t("RewardRuleSummaryWindow", {
          start: formatRuleDay(rule.startsOn, false),
          end: formatRuleDay(rule.endsOn),
        })
      : "";
  const cap =
    rule.monthlyCap && rule.capCurrency
      ? t("RewardRuleSummaryCap", {
          cap: formatCashFull(rule.monthlyCap, rule.capCurrency),
        })
      : "";
  return t("RewardRuleSummary", {
    who,
    rate: rule.ratePercent,
    what,
    when,
    cap,
  });
}

type OverlapShape = Pick<
  PlatformRewardRule,
  | "id"
  | "name"
  | "scope"
  | "defaultCategoryId"
  | "merchantId"
  | "companies"
  | "priority"
  | "startsOn"
  | "endsOn"
  | "status"
>;

function couldMatchSamePurchase(a: OverlapShape, b: OverlapShape): boolean {
  const companiesMeet =
    !a.companies.length ||
    !b.companies.length ||
    a.companies.some((company) =>
      b.companies.some((other) => other.id === company.id),
    );
  if (!companiesMeet) return false;
  if (a.scope === "ALL" || b.scope === "ALL") return true;
  if (a.scope === "CATEGORY" && b.scope === "CATEGORY") {
    return a.defaultCategoryId === b.defaultCategoryId;
  }
  if (a.scope === "MERCHANT" && b.scope === "MERCHANT") {
    return a.merchantId === b.merchantId;
  }
  return true;
}

// Overlapping dated rules with each pair's winner; expects the server's precedence order, which breaks priority ties
export function promotionOverlaps<T extends OverlapShape>(
  rules: T[],
): { first: T; second: T; winner: T }[] {
  const dated = rules.filter(
    (rule) =>
      rule.startsOn &&
      rule.endsOn &&
      (rule.status === "LIVE" || rule.status === "SCHEDULED"),
  );
  const overlaps: { first: T; second: T; winner: T }[] = [];
  dated.forEach((first, index) => {
    dated.slice(index + 1).forEach((second) => {
      const shareDays =
        (first.startsOn as string) <= (second.endsOn as string) &&
        (second.startsOn as string) <= (first.endsOn as string);
      if (!shareDays || !couldMatchSamePurchase(first, second)) return;
      overlaps.push({
        first,
        second,
        winner: second.priority < first.priority ? second : first,
      });
    });
  });
  return overlaps;
}
